import type { ChatDiscussionMode, ChatTopicsDisplayMode, IChatTopic } from './IChat'
import type { Reaction } from './reaction'
import type { ReplyTo } from './replyTo'
import type { ChatMember } from './member'
import type { User } from './user'

export interface ChatAttachment {
  id: number
  file: string
  mime_type?: string
  name?: string
  /** Matrix `info.thumbnail_url` — JPEG/постер для видео в ленте. */
  thumbnail?: string
}

export interface ChatMessage {
  id?: number | string
  matrix_event_id?: string
  message: string
  time: string
  senderId: number | string
  feedback: {
    isSent: boolean
    isDelivered: boolean
    isSeen: boolean
  }
  attachments?: ChatAttachment[]
  reactions?: Reaction[]
  reply_to?: ReplyTo
  reply_to_id?: number | string
  reply_message_id?: number | string
  created_by?: User
  avatar?: string
  is_edited?: boolean
  is_deleted?: boolean
  is_system?: boolean
  system_target_event_id?: string
  /** MXID упомянутых пользователей (`m.mentions.user_ids`) */
  mention_user_ids?: string[]
  forward?: {
    forward_id?: string
    original_sender_display?: string
    source_room_id?: string
    source_event_id?: string
  }
}

export interface Chat {
  id: number
  userId: number
  unseenMsgs: number
  messages: ChatMessage[]
}

export interface LastMessage {
  message: string
  time: string
  senderId: number | string
  feedback: {
    isSent: boolean
    isDelivered: boolean
    isSeen: boolean
  }
  attachments?: ChatAttachment[]
}

export interface BaseChatOut {
  id: Chat['id']
  unseenMsgs: Chat['unseenMsgs']
  messages: ChatMessage[]
  lastMessage: LastMessage
  is_pinned?: boolean
  pinned_at?: string | null
}

export interface GroupChatMetadata {
  type: 'group' | 'channel' | 'workgroup'
  title: string
  description?: string
  icon?: string | null
  members: ChatMember[]
  owner: string
  created_at: string
  created_by: User
  matrix_room_id?: string
  total_members?: number
  parent_space_id?: string
  discussion_mode?: ChatDiscussionMode
  topics_display_mode?: ChatTopicsDisplayMode
  topics?: IChatTopic[]
}

export interface SpaceChatOut extends BaseChatOut {
  type: 'space'
  title: string
  description?: string
  icon?: string | null
  members: ChatMember[]
  owner: string
  created_at: string
  created_by: User
  matrix_room_id?: string
  child_room_ids?: string[]
  total_members?: number
}

export interface DialogChatOut extends BaseChatOut {
  type?: 'dialog' | 'direct'
  matrix_room_id?: string
  matrix_peer_id?: string
}

export interface GroupChatOut extends BaseChatOut, GroupChatMetadata {
  type: 'group' | 'channel' | 'workgroup'
}

export interface EventChatOut extends BaseChatOut, Omit<GroupChatMetadata, 'type'> {
  type: 'event'
  event_url?: string | null
}

export type ChatOut = DialogChatOut | GroupChatOut | EventChatOut | SpaceChatOut

export const isGroupChat = (chat: ChatOut): chat is GroupChatOut => {
  return 'type' in chat
    && (chat.type === 'group' || chat.type === 'channel' || chat.type === 'workgroup')
}

export const isEventChat = (chat: ChatOut): chat is EventChatOut => {
  return 'type' in chat && chat.type === 'event'
}

export const isSpaceChat = (chat: ChatOut): chat is SpaceChatOut => {
  return 'type' in chat && chat.type === 'space'
}

export const isGroupLikeChat = (chat: ChatOut): chat is GroupChatOut | EventChatOut => {
  return isGroupChat(chat) || isEventChat(chat)
}

export const isDialogChat = (chat: ChatOut): chat is DialogChatOut => {
  return !('type' in chat) || chat.type === 'dialog' || chat.type === 'direct'
}
